import { HttpsAgent } from "agentkeepalive"
import axios, { AxiosRequestConfig } from "axios"
import axiosRetry, { IAxiosRetryConfig } from "axios-retry"

export interface commonAxiosResponse<T = any> {
  success: boolean
  message?: string
  data?: T
}

const isBrowser = typeof window !== "undefined"

export const defaultHttpsAgent = new HttpsAgent({ keepAlive: true })
export const defaultTimeout = 40000

// In-memory token storage — intentionally cleared on page reload
let _accessToken: string | null = null
const _accountTokens: Map<string, string> = new Map()

export const getAccessToken = (): string | null => _accessToken
export const setAccessToken = (token: string | null): void => {
  _accessToken = token
}
export const getAccountToken = (accountId: string): string | null =>
  _accountTokens.get(accountId) ?? null
export const setAccountTokens = (accounts: Array<{ id: string; authToken: string }>): void => {
  accounts.forEach(({ id, authToken }) => _accountTokens.set(id, authToken))
}

export const defaultConfig: AxiosRequestConfig = {
  timeout: defaultTimeout,
  ...(isBrowser ? {} : { httpsAgent: defaultHttpsAgent }),
  headers: {
    app: "Gunsens Admin Panel",
  },
}

const apiClient = axios.create({
  baseURL: process.env.REACT_APP_API_BASE_URL || "",
  ...defaultConfig,
})

// Retry reads only. The previous `|| error.response?.status >= 500` clause applied to
// every method, which is unsafe against this API for two reasons: gs-backend-api returns
// HTTP 500 for ordinary logical failures (its `errorMessage` helper defaults to 500, so
// "Owner not found" is a 500), and several billing POSTs have side effects that are not
// idempotent across separate calls. `POST /payment/DirectSubscribe` creates a new
// SOSBilling row per call and the backend keys its Stripe idempotency on that row id, so
// an auto-retried POST can charge a customer twice.
//
// "Retrying a GET costs nothing" isn't quite universal either - GET /payment/newsetup/:id
// mints a Stripe customer and billing-portal session, and GET /payment/sendMonitoringSuspensionEmail
// runs a group-wide suspend-and-email sweep. Neither is a charge, and newsetup's customer
// creation is idempotency-keyed, so the residual risk is low - but it's not literally free.
const RETRYABLE_METHODS = ["get", "head", "options"]

// gs-backend-api uses body-based POST for most list/filter reads rather than GET with
// query params. An explicit list of the path segments this dashboard actually calls that
// way, rather than matching the /get[A-Z]/ naming convention with a regex: the convention
// isn't enforced anywhere, so a regex was silently trusting every future getXyz route (in
// this repo or the backend) to also be a safe read, with nothing checking that assumption.
// Add to this list only when a call site that genuinely needs retrying is added.
const IDEMPOTENT_POST_PATH_SEGMENTS = new Set([
  "getLocations",
  "getAllSensors",
  "getFloorPlans",
  "getSensors",
  "getSensorEvents",
  "getAllLocations",
  "getLocationDetails",
  "getSensorById",
  "getCustomers",
  "getBills",
  "getInvoice",
  "getAllDevices",
  "getPaySettingDetails",
  "getVersions",
  "getAcceptedTerms",
])

// Exported so a test can pin both sides of this: the reads that must stay retryable,
// and - more importantly for a payment surface - the writes that must never become so.
export const isIdempotentPostPath = (url?: string): boolean => {
  if (!url) return false
  return url
    .split("?")[0]
    .split("/")
    .some((segment) => IDEMPOTENT_POST_PATH_SEGMENTS.has(segment))
}

axiosRetry(apiClient, {
  retries: 3,
  retryDelay: axiosRetry.exponentialDelay,
  retryCondition: (error: any) => {
    const method = (error.config?.method ?? "get").toLowerCase()
    const retryable =
      RETRYABLE_METHODS.includes(method) ||
      (method === "post" && isIdempotentPostPath(error.config?.url))
    if (!retryable) return false

    return axiosRetry.isNetworkOrIdempotentRequestError(error) || error.response?.status >= 500
  },
} as IAxiosRetryConfig)

apiClient.interceptors.request.use((config) => {
  // Refresh endpoint authenticates via cookie only — never send the (possibly expired) access token
  if (config.url === "/auth/refresh") return config
  const token = getAccessToken()
  if (token) {
    config.headers.Authorization = `Bearer ${token}`
  }
  return config
})

let isRefreshing = false
let failedQueue: Array<{ resolve: (token: string) => void; reject: (err: any) => void }> = []

const processQueue = (error: any, token: string | null = null): void => {
  failedQueue.forEach(({ resolve, reject }) => {
    if (error) reject(error)
    else resolve(token!)
  })
  failedQueue = []
}

apiClient.interceptors.response.use(
  (res) => res,
  async (err) => {
    const originalRequest = err.config

    // Skip refresh loop for the refresh endpoint itself or already-retried requests
    if (
      err.response?.status === 401 &&
      !originalRequest._retry &&
      originalRequest.url !== "/auth/refresh"
    ) {
      if (isRefreshing) {
        // Queue this request until the in-flight refresh completes
        return new Promise<string>((resolve, reject) => {
          failedQueue.push({ resolve, reject })
        })
          .then((token) => {
            originalRequest.headers.Authorization = `Bearer ${token}`
            return apiClient(originalRequest)
          })
          .catch((queueErr) => Promise.reject(queueErr))
      }

      originalRequest._retry = true
      isRefreshing = true

      try {
        const res = await apiClient.post("/auth/refresh", undefined, { withCredentials: true })
        const responseData = res.data?.data || {}
        const { authToken, accounts } = responseData
        setAccessToken(authToken)
        if (accounts?.length) setAccountTokens(accounts)
        processQueue(null, authToken)
        originalRequest.headers.Authorization = `Bearer ${authToken}`
        return apiClient(originalRequest)
      } catch (refreshError) {
        processQueue(refreshError, null)
        window.location.href = "/login"
        return Promise.reject(refreshError)
      } finally {
        isRefreshing = false
      }
    }

    // Surface the API's own message. gs-backend-api reports failures as a non-2xx with a
    // useful `message` in the body (its `errorMessage` helper defaults to HTTP 500, so
    // even "Owner not found" arrives that way). Axios rejects with a generic "Request
    // failed with status code 500", and pages toast `error.message`, so without this the
    // explanatory text the backend went to the trouble of writing never reaches the admin.
    const apiMessage = err.response?.data?.message
    if (typeof apiMessage === "string" && apiMessage.trim() !== "") {
      err.message = apiMessage
    }

    return Promise.reject(err)
  },
)

export default apiClient
